/** Admin: templates and shared state lifecycle (spec section 13). */
import { z } from 'zod';
import { ContentReleaseIdSchema, TemplateIdSchema, TemplateVersionIdSchema } from '../../ids.js';
import { CountSchema, CursorQuerySchema, EmptyRequestSchema, IsoDateTimeSchema, NoQuerySchema, RevisionSchema, SemVerRangeSchema, defineRoute, pageOf } from '../common.js';
import {
  ContentReleaseSchema,
  ReleaseChannelSchema,
  RolloutStageSchema,
  TemplateSchema,
  TemplateTypeSchema,
  TemplateVersionSchema,
  TemplateVersionStateSchema,
} from '../entities.js';

export const TemplateListQuerySchema = CursorQuerySchema.extend({
  type: TemplateTypeSchema.optional(),
  state: TemplateVersionStateSchema.optional(),
  search: z.string().max(120).optional(),
});
export const TemplatePageSchema = pageOf(TemplateSchema);

export const TemplateDetailResponseSchema = z.object({
  template: TemplateSchema,
  activeVersion: TemplateVersionSchema.nullable(),
  latestVersion: TemplateVersionSchema,
});
export type TemplateDetailResponse = z.infer<typeof TemplateDetailResponseSchema>;

export const TemplateCreateDraftRequestSchema = z
  .object({
    type: TemplateTypeSchema,
    /** Optional stable id; generated from name when absent. */
    id: TemplateIdSchema.optional(),
    name: z.string().min(1).max(160),
    description: z.string().max(1000).optional(),
    body: z.unknown(),
    changeSummary: z.string().min(1).max(1000),
    /** Start the draft from an existing version's body. */
    fromVersionId: TemplateVersionIdSchema.optional(),
  })
  .strict();
export type TemplateCreateDraftRequest = z.infer<typeof TemplateCreateDraftRequestSchema>;

export const TemplateUpdateRequestSchema = z
  .object({
    body: z.unknown(),
    changeSummary: z.string().min(1).max(1000),
    name: z.string().min(1).max(160).optional(),
    description: z.string().max(1000).nullable().optional(),
    revision: RevisionSchema,
  })
  .strict();

export const ValidationIssueSchema = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
  severity: z.enum(['error', 'warning']),
});
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

export const TemplateValidateResponseSchema = z.object({
  version: TemplateVersionSchema,
  valid: z.boolean(),
  issues: z.array(ValidationIssueSchema),
  /** Variables referenced by the body and whether the variable schema knows them. */
  variables: z.array(z.object({ name: z.string(), known: z.boolean() })),
});
export type TemplateValidateResponse = z.infer<typeof TemplateValidateResponseSchema>;

export const TemplatePreviewRequestSchema = z
  .object({
    /** Synthetic fixture tenant to render against (never customer data). */
    fixtureTenantId: z.string().max(64).optional(),
    fixtureSize: z.enum(['S', 'M', 'L']).default('S'),
    params: z.record(z.string(), z.unknown()).default({}),
  })
  .strict();

export const TemplatePreviewResponseSchema = z.object({
  version: TemplateVersionSchema,
  /** Rendered preview: restricted Markdown for text content, JSON for configuration types. */
  rendered: z.union([z.string(), z.record(z.string(), z.unknown())]),
  affectedRules: z.array(z.object({ canonicalKey: z.string(), change: z.enum(['added', 'removed', 'changed']) })),
  warnings: z.array(z.string()),
  renderedAt: IsoDateTimeSchema,
});
export type TemplatePreviewResponse = z.infer<typeof TemplatePreviewResponseSchema>;

export const TemplatePublishRequestSchema = z
  .object({
    channel: ReleaseChannelSchema.default('stable'),
    changeSummary: z.string().min(1).max(2000),
    compatibleAppVersions: SemVerRangeSchema,
    /** Initial rollout stage; default first cohort. */
    rolloutStage: RolloutStageSchema.default('cohort_3'),
    revision: RevisionSchema,
  })
  .strict();
export type TemplatePublishRequest = z.infer<typeof TemplatePublishRequestSchema>;

export const TemplatePublishResponseSchema = z.object({
  version: TemplateVersionSchema,
  release: ContentReleaseSchema,
});

export const TemplateRollbackRequestSchema = z
  .object({
    toVersionId: TemplateVersionIdSchema,
    changeSummary: z.string().min(1).max(2000),
    revision: RevisionSchema,
  })
  .strict();

export const TemplateVersionsResponseSchema = z.object({ items: z.array(TemplateVersionSchema) });

export const TemplateDiffQuerySchema = z.object({
  from: TemplateVersionIdSchema,
  to: TemplateVersionIdSchema,
});

export const DiffHunkSchema = z.object({
  kind: z.enum(['equal', 'insert', 'delete']),
  path: z.string().nullable(),
  text: z.string(),
});
export const TemplateDiffResponseSchema = z.object({
  from: TemplateVersionSchema,
  to: TemplateVersionSchema,
  hunks: z.array(DiffHunkSchema),
  /** Structural JSON changes for configuration types. */
  changedPaths: z.array(z.object({ path: z.string(), before: z.unknown(), after: z.unknown() })),
});
export type TemplateDiffResponse = z.infer<typeof TemplateDiffResponseSchema>;

export const TemplateParamsSchema = z.object({ templateId: TemplateIdSchema });
export const TemplateVersionParamsSchema = z.object({ templateId: TemplateIdSchema, versionId: TemplateVersionIdSchema });
export const TemplateVersionResponseSchema = z.object({ version: TemplateVersionSchema });

export const ContentReleaseListQuerySchema = CursorQuerySchema.extend({ channel: ReleaseChannelSchema.optional() });
export const ContentReleasePageSchema = pageOf(ContentReleaseSchema);
export const ContentReleaseParamsSchema = z.object({ releaseId: ContentReleaseIdSchema });
export const ContentReleaseResponseSchema = z.object({
  release: ContentReleaseSchema,
  adoptions: z.array(z.object({ appVersion: z.string(), count: CountSchema, rejected: CountSchema })),
});
export const ContentRolloutRequestSchema = z.object({ stage: RolloutStageSchema }).strict();
// added by server: release-level roll back / activate (ADMIN_UI_SPEC Releases page)
export const ContentReleaseRollbackRequestSchema = z.object({ changeSummary: z.string().max(2000).optional() }).strict();

const templateRead = ['template.edit', 'template.publish'] as const;

export const adminTemplateRoutes = {
  'admin.templates.list': defineRoute({ id: 'admin.templates.list', method: 'GET', path: '/admin/v1/templates', auth: 'admin', capability: templateRead, requestSchema: TemplateListQuerySchema, responseSchema: TemplatePageSchema, description: 'List templates by type/state.' }),
  'admin.templates.get': defineRoute({ id: 'admin.templates.get', method: 'GET', path: '/admin/v1/templates/:templateId', auth: 'admin', capability: templateRead, paramsSchema: TemplateParamsSchema, requestSchema: NoQuerySchema, responseSchema: TemplateDetailResponseSchema, description: 'Template with active and latest versions.' }),
  'admin.templates.createDraft': defineRoute({ id: 'admin.templates.createDraft', method: 'POST', path: '/admin/v1/templates', auth: 'admin', capability: 'template.edit', requestSchema: TemplateCreateDraftRequestSchema, responseSchema: TemplateVersionResponseSchema, description: 'Create a draft version (new template or next version of an existing one).' }),
  'admin.templates.update': defineRoute({ id: 'admin.templates.update', method: 'PUT', path: '/admin/v1/templates/:templateId/versions/:versionId', auth: 'admin', capability: 'template.edit', paramsSchema: TemplateVersionParamsSchema, requestSchema: TemplateUpdateRequestSchema, responseSchema: TemplateVersionResponseSchema, description: 'Update a draft body; resets state to draft.' }),
  'admin.templates.validate': defineRoute({ id: 'admin.templates.validate', method: 'POST', path: '/admin/v1/templates/:templateId/versions/:versionId/validate', auth: 'admin', capability: 'template.edit', paramsSchema: TemplateVersionParamsSchema, requestSchema: EmptyRequestSchema, responseSchema: TemplateValidateResponseSchema, description: 'Schema validation; rejects executable content. draft -> validated.' }),
  'admin.templates.preview': defineRoute({ id: 'admin.templates.preview', method: 'POST', path: '/admin/v1/templates/:templateId/versions/:versionId/preview', auth: 'admin', capability: templateRead, paramsSchema: TemplateVersionParamsSchema, requestSchema: TemplatePreviewRequestSchema, responseSchema: TemplatePreviewResponseSchema, description: 'Render against synthetic fixtures. validated -> previewed.' }),
  'admin.templates.submitReview': defineRoute({ id: 'admin.templates.submitReview', method: 'POST', path: '/admin/v1/templates/:templateId/versions/:versionId/submit-review', auth: 'admin', capability: 'template.edit', paramsSchema: TemplateVersionParamsSchema, requestSchema: z.object({ revision: RevisionSchema }).strict(), responseSchema: TemplateVersionResponseSchema, description: 'previewed -> reviewed (ready for a publisher).' }),
  'admin.templates.publish': defineRoute({ id: 'admin.templates.publish', method: 'POST', path: '/admin/v1/templates/:templateId/versions/:versionId/publish', auth: 'admin', capability: 'template.publish', recentAuth: true, paramsSchema: TemplateVersionParamsSchema, requestSchema: TemplatePublishRequestSchema, responseSchema: TemplatePublishResponseSchema, description: 'Publish an immutable version and sign a new content release. reviewed -> published/active.' }),
  'admin.templates.rollback': defineRoute({ id: 'admin.templates.rollback', method: 'POST', path: '/admin/v1/templates/:templateId/rollback', auth: 'admin', capability: 'template.publish', recentAuth: true, paramsSchema: TemplateParamsSchema, requestSchema: TemplateRollbackRequestSchema, responseSchema: TemplatePublishResponseSchema, description: 'Republish an earlier valid version as active; history is retained.' }),
  'admin.templates.versions': defineRoute({ id: 'admin.templates.versions', method: 'GET', path: '/admin/v1/templates/:templateId/versions', auth: 'admin', capability: templateRead, paramsSchema: TemplateParamsSchema, requestSchema: NoQuerySchema, responseSchema: TemplateVersionsResponseSchema, description: 'All versions, newest first.' }),
  'admin.templates.diff': defineRoute({ id: 'admin.templates.diff', method: 'GET', path: '/admin/v1/templates/:templateId/diff', auth: 'admin', capability: templateRead, paramsSchema: TemplateParamsSchema, requestSchema: TemplateDiffQuerySchema, responseSchema: TemplateDiffResponseSchema, description: 'Diff two versions.' }),
  'admin.content.releases.list': defineRoute({ id: 'admin.content.releases.list', method: 'GET', path: '/admin/v1/content/releases', auth: 'admin', capability: templateRead, requestSchema: ContentReleaseListQuerySchema, responseSchema: ContentReleasePageSchema, description: 'Content releases with rollout stage and adoption counts.' }),
  'admin.content.releases.get': defineRoute({ id: 'admin.content.releases.get', method: 'GET', path: '/admin/v1/content/releases/:releaseId', auth: 'admin', capability: templateRead, paramsSchema: ContentReleaseParamsSchema, requestSchema: NoQuerySchema, responseSchema: ContentReleaseResponseSchema, description: 'One release with adoption breakdown.' }),
  'admin.content.releases.setRollout': defineRoute({ id: 'admin.content.releases.setRollout', method: 'POST', path: '/admin/v1/content/releases/:releaseId/rollout', auth: 'admin', capability: 'template.publish', recentAuth: true, paramsSchema: ContentReleaseParamsSchema, requestSchema: ContentRolloutRequestSchema, responseSchema: ContentReleaseResponseSchema, description: 'Advance or halt the staged rollout.' }),
  // added by server
  'admin.content.releases.rollback': defineRoute({ id: 'admin.content.releases.rollback', method: 'POST', path: '/admin/v1/content/releases/:releaseId/rollback', auth: 'admin', capability: 'template.publish', recentAuth: true, paramsSchema: ContentReleaseParamsSchema, requestSchema: ContentReleaseRollbackRequestSchema, responseSchema: ContentReleaseResponseSchema, description: 'Roll back a release: it stops distributing, the previous valid release becomes active again and templates point back at its versions. History is retained.' }),
  'admin.content.releases.activate': defineRoute({ id: 'admin.content.releases.activate', method: 'POST', path: '/admin/v1/content/releases/:releaseId/activate', auth: 'admin', capability: 'template.publish', recentAuth: true, paramsSchema: ContentReleaseParamsSchema, requestSchema: EmptyRequestSchema, responseSchema: ContentReleaseResponseSchema, description: 'Make a release the active one for everyone: rollout stage all; newer releases on the channel are halted.' }),
} as const;
